import { useEffect, useState } from 'react';
import api from '../../api/client';
import useWebSocket from '../../api/websocket';
import useRadioControl from '../../hooks/useRadioControl';
import ListenersBadge from './ListenersBadge';
import ProgramStrip from './ProgramStrip';

function useArgentinaClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);
  return new Intl.DateTimeFormat('es-AR', {
    timeZone: 'America/Argentina/Buenos_Aires',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(now);
}

export default function StatusBar({ canControl = true }) {
  const clock = useArgentinaClock();
  const [nowPlaying, setNowPlaying] = useState(null);
  const { status, busy, toggle } = useRadioControl();

  const { subscribe } = useWebSocket((data) => {
    if (data.type === 'now_playing') setNowPlaying(data.payload);
  });
  useEffect(() => {
    const unsub = subscribe('now_playing', (data) => setNowPlaying(data.payload));
    return unsub;
  }, [subscribe]);

  const online = status === 'ONLINE';

  // Apagar la radio corta el stream para todos los oyentes: pedir confirmación.
  const handleToggle = () => {
    if (online && !window.confirm('⚠️ ¿Detener la radio?\n\nEl streaming se cortará para todos los oyentes hasta que la vuelvas a encender. ¿Continuar?')) {
      return;
    }
    toggle();
  };

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-dark-700 bg-dark-850 px-4 py-2 text-sm">
      <div className="flex shrink-0 items-center gap-3">
        {canControl ? (
          <button
            type="button"
            onClick={handleToggle}
            disabled={busy}
            title={online ? 'Apagar la radio (pide confirmación)' : 'Encender la radio'}
            className={`flex h-9 shrink-0 items-center gap-2 rounded-md border px-3 transition-[filter] ${
              online
                ? 'border-success-500 bg-success-600 shadow-[0_0_10px_rgba(22,163,74,.55)]'
                : 'border-error-500 bg-error-600 shadow-[0_0_10px_rgba(220,38,38,.55)]'
            } ${busy ? 'opacity-60' : 'hover:brightness-110'}`}
          >
            <span className="relative flex h-2.5 w-2.5">
              {online && (
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75"></span>
              )}
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white"></span>
            </span>
            <span className="text-lg font-extrabold uppercase leading-none tracking-wider text-white">
              {online ? 'ONLINE' : 'OFFLINE'}
            </span>
          </button>
        ) : (
          <span
            className={`flex h-9 shrink-0 items-center gap-2 rounded-md border px-3 ${
              online ? 'border-success-500 bg-success-600' : 'border-error-500 bg-error-600'
            }`}
          >
            <span className={`inline-flex h-2.5 w-2.5 rounded-full bg-white`}></span>
            <span className="text-lg font-extrabold uppercase leading-none tracking-wider text-white">
              {online ? 'ONLINE' : 'OFFLINE'}
            </span>
          </span>
        )}

        {/* Oyentes totales en vivo (cartel rojo, visible en todas las secciones) */}
        <ListenersBadge />

        {/* Reloj digital (fondo negro, números blancos) */}
        <span className="flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-dark-600 bg-black px-2.5 shadow-[0_0_10px_rgba(0,0,0,.6)]">
          <span
            className="font-mono text-xl font-extrabold leading-none tabular-nums tracking-widest text-white"
            style={{ textShadow: '0 0 8px rgba(255,255,255,.35)' }}
          >
            {clock}
          </span>
          <span className="text-[10px] font-bold text-dark-400">ARG</span>
        </span>
      </div>

      {/* Tira de programación: 12 h pasadas | AHORA (rojo) | próximas 12 h.
          Scroll con la rueda del mouse, sin scrollbar. */}
      <ProgramStrip />

      <div className="flex min-w-0 max-w-[240px] items-center gap-2">
        <span className="shrink-0 text-dark-500">Sonando:</span>
        {nowPlaying ? (
          <span className="truncate font-medium text-dark-100" title={`${nowPlaying.artist || ''} - ${nowPlaying.title || ''}`}>
            {nowPlaying.artist ? `${nowPlaying.artist} - ` : ''}
            {nowPlaying.title || '—'}
            {nowPlaying.program_name ? <span className="text-dark-500"> · {nowPlaying.program_name}</span> : null}
          </span>
        ) : (
          <span className="text-dark-500">—</span>
        )}
      </div>
    </div>
  );
}
